import React from 'react';
import { FadeIn } from './ui/FadeIn';
import { CheckCircle2, XCircle, BookOpen, Sun, Moon, MessageCircle, FileText, Clock, Shield } from 'lucide-react';
export function DetailsSection() {
  return <>
      {/* Why it works */}
      <section className="py-20 px-4 bg-[#FAF8F5]" data-id="element-7">
        <div className="max-w-3xl mx-auto text-center" data-id="element-8">
          <FadeIn data-id="element-9">
            <h3 className="text-2xl md:text-3xl font-serif text-[#3D3D3D] mb-6" data-id="element-10">
              ¿Por qué funciona?
            </h3>
            <p className="text-lg text-[#5A6B5A] mb-8" data-id="element-11">
              Porque no le pide a tu "mejor versión" que aparezca. <br data-id="element-12" />
              <strong className="text-[#C4836A]" data-id="element-13">
                Funciona incluso con tu versión cansada
              </strong>
              , la que está sosteniendo todo.
            </p>
            <p className="text-[#5A6B5A]" data-id="element-14">
              Y porque ataca los 3 frentes que mantienen la ansiedad prendida:
              cuerpo, mente y entorno. No uno. Los tres.
            </p>
          </FadeIn>
        </div>
      </section>

      {/* What's included */}
      <section className="py-20 px-4 bg-white" data-id="element-15">
        <div className="max-w-5xl mx-auto" data-id="element-16">
          <FadeIn data-id="element-17">
            <h3 className="text-3xl md:text-4xl font-serif text-[#3D3D3D] mb-12 text-center" data-id="element-18">
              Qué incluye
            </h3>
          </FadeIn>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-19">
            {[{
            icon: BookOpen,
            title: 'Protocolo 3×3×3',
            desc: 'Explicado sin rollo teórico'
          }, {
            icon: Sun,
            title: 'Ritual de Arranque',
            desc: 'Para no iniciar en modo incendio'
          }, {
            icon: Moon,
            title: 'Ritual de Corte',
            desc: 'Apaga el día antes de que te apague'
          }, {
            icon: MessageCircle,
            title: '12 guiones antirumiar',
            desc: 'Frases exactas para cortar bucles'
          }, {
            icon: FileText,
            title: 'Plantillas rápidas',
            desc: 'Para decisiones de tiempo y límites'
          }, {
            icon: Clock,
            title: 'Agenda Día 1–14',
            desc: 'Con enfoque diario paso a paso'
          }, {
            icon: Shield,
            title: 'Modo SOS 5 min',
            desc: 'Para cuando ya no puedes más'
          }, {
            icon: CheckCircle2,
            title: 'Plan 7×7',
            desc: 'Para sostenerlo sin rigidez'
          }].map((item, index) => <FadeIn key={index} delay={index * 0.05} data-id="element-20">
                <div className="p-6 bg-[#FAF8F5] rounded-xl h-full hover:shadow-md transition-shadow duration-300" data-id="element-21">
                  <item.icon className="w-8 h-8 text-[#8B9E8B] mb-4" data-id="element-22" />
                  <h4 className="font-bold text-[#3D3D3D] mb-2" data-id="element-23">
                    {item.title}
                  </h4>
                  <p className="text-sm text-[#5A6B5A]" data-id="element-24">{item.desc}</p>
                </div>
              </FadeIn>)}
          </div>
        </div>
      </section>

      {/* What it is / isn't */}
      <section className="py-20 px-4 bg-[#FAF8F5]" data-id="element-25">
        <div className="max-w-5xl mx-auto" data-id="element-26">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12" data-id="element-27">
            {/* Not */}
            <FadeIn direction="right" data-id="element-28">
              <div className="bg-white p-8 rounded-2xl shadow-sm border border-red-100" data-id="element-29">
                <h3 className="text-xl font-serif text-[#3D3D3D] mb-6 flex items-center gap-2" data-id="element-30">
                  <XCircle className="text-red-400" data-id="element-31" /> Lo que NO es
                </h3>
                <ul className="space-y-4" data-id="element-32">
                  {['No es terapia.', "No es 'manifestación'.", 'No es un reto de disciplina.', 'No es una rutina eterna.'].map((item, i) => <li key={i} className="flex items-center gap-3 text-[#5A6B5A]" data-id="element-33">
                      <span className="w-1.5 h-1.5 rounded-full bg-red-300" data-id="element-34"></span>
                      {item}
                    </li>)}
                </ul>
                <p className="mt-6 text-[#3D3D3D] font-medium italic" data-id="element-35">
                  Es un sistema mínimo para recuperar tu centro en una vida que
                  no se va a pausar.
                </p>
              </div>
            </FadeIn>

            {/* Is for / Not for */}
            <FadeIn direction="left" data-id="element-36">
              <div className="bg-white p-8 rounded-2xl shadow-sm border border-[#E5E0D8]" data-id="element-37">
                <h3 className="text-xl font-serif text-[#3D3D3D] mb-6 flex items-center gap-2" data-id="element-38">
                  <CheckCircle2 className="text-[#8B9E8B]" data-id="element-39" /> Para quién es
                </h3>
                <div className="space-y-6" data-id="element-40">
                  <div data-id="element-41">
                    <h4 className="font-bold text-[#3D3D3D] mb-2 text-sm uppercase tracking-wide" data-id="element-42">
                      Es para ti si:
                    </h4>
                    <p className="text-[#5A6B5A]" data-id="element-43">
                      Eres mujer 30+, estás saturada, quieres herramientas
                      prácticas y cortas, y estás lista para hacer algo pequeño
                      pero constante.
                    </p>
                  </div>
                  <div className="pt-4 border-t border-gray-100" data-id="element-44">
                    <h4 className="font-bold text-[#3D3D3D] mb-2 text-sm uppercase tracking-wide text-gray-400" data-id="element-45">
                      No es para ti si:
                    </h4>
                    <p className="text-[#5A6B5A]" data-id="element-46">
                      Buscas una solución clínica profunda aquí, o quieres una
                      promesa mágica de "nunca más ansiedad".
                    </p>
                  </div>
                </div>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* Promise */}
      <section className="py-20 px-4 bg-[#8B9E8B] text-white text-center" data-id="element-47">
        <div className="max-w-3xl mx-auto" data-id="element-48">
          <FadeIn data-id="element-49">
            <h3 className="text-2xl md:text-3xl font-serif mb-6" data-id="element-50">
              Lo que cambia en 14 días (sin exagerar)
            </h3>
            <p className="text-lg md:text-xl font-light opacity-90 mb-8" data-id="element-51">
              No prometo que tu vida se vuelva fácil. <br data-id="element-52" />
              Prometo algo más útil:
            </p>
            <p className="text-2xl md:text-3xl font-serif font-medium" data-id="element-53">
              Que vuelvas a sentir que{' '}
              <span className="underline decoration-[#C4836A] decoration-4 underline-offset-4" data-id="element-54">
                tú
              </span>{' '}
              estás al mando… al menos dos veces al día.
            </p>
          </FadeIn>
        </div>
      </section>
    </>;
}